// src/components/features/departures/DepartureList/DepartureList.tsx
import type { ComponentProps } from 'preact';
import { Departure, type DepartureModel } from '../DepartureItem/DepartureItem';
import { CountdownBar } from '../CountdownBar/CountdownBar';
import { Button } from '../../../base/Button/Button';
import { SvgIcon } from '../../../base/Icon/SvgIcon';
import { PagePadding } from '../../../widget/Pages/Pages';

// styles
import styles from './DepartureList.module.css';

export interface DepartureListProps extends Omit<
  ComponentProps<'section'>,
  'children' | 'className'
> {
  departures: DepartureModel[];
  autoRefreshEnabled?: boolean;
  onAutoRefreshChange?: (enabled: boolean) => void;
  onRefresh?: () => void;
  countdownSecondsRemaining?: number | null;
  paused?: boolean;
  lastUpdatedTime?: string | null;
}

/**
 * Renders a collection of departures for a selected stop.
 *
 * Intended to contain multiple Departure rows.
 */
export function DepartureList(props: DepartureListProps) {
  const {
    autoRefreshEnabled = false,
    countdownSecondsRemaining,
    departures,
    lastUpdatedTime,
    onAutoRefreshChange,
    onRefresh,
    paused = false,
    ...rest
  } = props;

  const metaText =
    paused && lastUpdatedTime
      ? `Departures are paused. Last updated ${lastUpdatedTime}`
      : lastUpdatedTime
        ? `Last updated ${lastUpdatedTime}`
        : null;

  const handleAutoRefresh = () => {
    onAutoRefreshChange?.(!autoRefreshEnabled);
  };

  const handleManualRefresh = () => {
    onRefresh?.();
  };

  return (
    <section className={styles.container} {...rest}>
      <PagePadding>
        <div className={styles.autoRefreshContainer}>
          <div className={styles.meta}>
            {metaText && <span>{metaText}</span>}
          </div>

          <div className={styles.ctaContainer}>
            <Button
              fullWidth={true}
              onClick={handleManualRefresh}
              variant="secondary"
              disabled={!paused}
              aria-hidden={!paused}
              className={paused ? '' : styles.refreshHidden}
            >
              Refresh
            </Button>

            <Button
              fullWidth={true}
              onClick={handleAutoRefresh}
              variant="primary"
            >
              <div className={styles.buttonContent}>
                <div className={styles.buttonLabel}>Auto refresh</div>
                <span className={styles.checkboxWrapper}>
                  <span
                    className={`${styles.checkboxVisual} ${
                      autoRefreshEnabled ? styles.checked : ''
                    }`}
                  >
                    {autoRefreshEnabled && (
                      <SvgIcon className={styles.check} name="check" />
                    )}
                  </span>
                </span>
              </div>
            </Button>
          </div>
        </div>
      </PagePadding>

      {/* Reserved space for countdown bar to reduce layout shift */}
      <div className={styles.countdownWrapper}>
        {countdownSecondsRemaining != null &&
          countdownSecondsRemaining >= 0 && (
            <CountdownBar secondsRemaining={countdownSecondsRemaining} />
          )}
      </div>

      <ul className={styles.list}>
        {departures.map((departure) => (
          <Departure key={departure.id} departure={departure} paused={paused} />
        ))}
      </ul>
    </section>
  );
}
